import {
    OffsetDateTime,
    ZoneOffset,
    LocalDate,
    ZonedDateTime as ZonedDateTimeClass,
    ZoneId,
    DateTimeFormatter,
} from '@js-joda/core';
import type { ZonedDateTime } from '@js-joda/core';
import { requestIdHeader } from '@thinkalpha/common/contracts/x-headers.js';
import type {
    Order,
    CancellationRequest,
    KillRequest,
    MassCancellationRequest,
    NewExecution,
    NewOrderBase,
} from '@thinkalpha/trading-ws-client/contracts/order.js';
import type { AxiosResponse } from 'axios';
import { createInstance } from 'src/api';
import { appConfig } from 'src/lib/config';
import { processQuery, getInitialSettings } from 'src/lib/paging';

const axios = createInstance({ baseURL: appConfig.tradingApi });

const buildRequestIdHeader = (requestId?: string): Record<typeof requestIdHeader, string> | object => {
    return requestId
        ? {
              [requestIdHeader]: requestId,
          }
        : {};
};

export function createExecution(execution: NewExecution | NewExecution[], requestId?: string): Promise<AxiosResponse> {
    const headers = buildRequestIdHeader(requestId);
    return axios.post('/orders/executions', execution, { headers: { ...headers } });
}

export const TIME_OFFSET = -5;

export const generateTodayOnlyRange = (): { fromDate: ZonedDateTime; untilDate: ZonedDateTime } => {
    const offsetDateTime = OffsetDateTime.now(ZoneOffset.ofHours(TIME_OFFSET));
    const fromDate = offsetDateTime.toLocalDate().atStartOfDay(offsetDateTime.offset());
    const untilDate = offsetDateTime.toLocalDate().plusDays(1).atStartOfDay(offsetDateTime.offset());

    return {
        fromDate,
        untilDate,
    };
};

export interface GetOrdersFilters {
    accounts?: string[];
    actors?: string[];
    todayOnly?: boolean;
    limit?: number;
}

export function getOrdersQueryParams({ accounts, todayOnly, actors, limit = 1000 }: GetOrdersFilters) {
    const params = [];

    if (accounts && accounts.length > 0) {
        accounts.forEach((account) => {
            params.push(`accounts[]=${encodeURIComponent(account)}`);
        });
    }

    if (actors && actors.length > 0) {
        actors.forEach((actor) => {
            params.push(`actors[]=${encodeURIComponent(actor)}`);
        });
    }

    if (todayOnly) {
        const queryParts = [];
        const { fromDate, untilDate } = generateTodayOnlyRange();

        queryParts.push(`createdAt gt '${fromDate.toInstant().toString()}'`);
        queryParts.push(`createdAt lt '${untilDate.toInstant().toString()}'`);

        const query = getInitialSettings(queryParts.join(' and '));

        query.limit = limit;
        query.includeMeta = false;

        params.push(processQuery(query).slice(2));
    }

    return params.length > 0 ? `?${params.join('&')}` : '';
}

export async function getOrders({
    accounts,
    actors,
    todayOnly = false,
    limit = 1000,
}: GetOrdersFilters): Promise<Order[]> {
    const params = getOrdersQueryParams({ accounts, actors, todayOnly, limit });

    const res = await axios.get(`/orders${params}`);
    return res.data;
}

export function sendOrder({ order, requestId }: { order: NewOrderBase; requestId?: string }): Promise<AxiosResponse> {
    const requestIdHeader = buildRequestIdHeader(requestId);
    return axios.post('/orders', order, { headers: { ...requestIdHeader } });
}

export function cancelOrder({ orderId, requestId }: { orderId: string; requestId?: string }): Promise<AxiosResponse> {
    const requestIdHeader = buildRequestIdHeader(requestId);
    return axios.delete(`/orders/${orderId}`, {
        headers: { ...requestIdHeader },
    });
}

export function cancelOrders({
    orders,
    requestId,
    text,
}: {
    orders: CancellationRequest['orders'];
    requestId?: string;
    text?: string;
}): Promise<AxiosResponse> {
    const requestIdHeader = buildRequestIdHeader(requestId);
    const payload: any = { orders };
    if (text !== undefined) {
        payload.text = text;
    }
    return axios.post('/orders/cancels', payload, { headers: { ...requestIdHeader } });
}

export function massCancelOrder({
    order,
    requestId,
}: {
    order: MassCancellationRequest;
    requestId?: string;
}): Promise<AxiosResponse> {
    const requestIdHeader = buildRequestIdHeader(requestId);
    return axios.post('/orders/mass-cancels', order, { headers: { ...requestIdHeader } });
}

export function killOrders({
    orders,
    requestId,
}: {
    orders: KillRequest['orders'];
    requestId?: string;
}): Promise<AxiosResponse> {
    const requestIdHeader = buildRequestIdHeader(requestId);
    return axios.post('/orders/kills', { orders }, { headers: { ...requestIdHeader } });
}

export function getGtdTimeForDate(dateString: string): string {
    const localDate = LocalDate.parse(dateString.substring(0, 10));
    const nyDateTime = ZonedDateTimeClass.of(
        localDate.year(),
        localDate.monthValue(),
        localDate.dayOfMonth(),
        0,
        1,
        0,
        0,
        ZoneId.of('America/New_York'),
    );
    const utcDateTime = nyDateTime.withZoneSameInstant(ZoneId.UTC);
    return utcDateTime.format(DateTimeFormatter.ofPattern("yyyy-MM-dd'T'HH:mm:ss'Z'"));
}
